import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Button,
  Card,
  TextField,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  InputAdornment,
} from '@material-ui/core';
import { Auth } from '@aws-amplify/auth';
import { useDispatch } from 'react-redux';
import { Alert } from '@material-ui/lab';

import CenterlineIcon from '../images/CenterlineAddInLogo.png';
import { fetchUser } from '../features/user/actions';
import { resetApiClient } from '../api-client/api-client';
import CircularLoader from '../main-components/loader/CircularLoader';
import ArchivedProjectLock from '../main-components/custom-components/ArchivedProjectLock';
import { getLoggedInUser, getPublicUserInfoByEmail } from '../models/api/users';
import { uploadToStaging } from '../models/api/filesystem';
import { getProjectDisplayName, projectSort, waitForFileToBeVerifiedByUrl } from '../scripts/utils';
import {
  AttachmentContent,
  EmailData,
  EmailDataBodyFormatEnum,
  ISimplifiedProjectView,
  IUser,
} from '../api-client/autogenerated';
import { fileEmailToProject, getProjectsSimplified } from '../models/api/project';
import { ExitToApp, CheckCircle, Error as ErrorIcon, Search } from '@material-ui/icons';

interface OfficeAttachment {
  id: string;
  name: string;
  size: number;
  contentType: string;
  attachmentType: Office.MailboxEnums.AttachmentType;
}

interface OfficeSelectedItemDetails {
  itemId: string;
  subject?: string;
  itemType?: string;
  itemMode?: string;
  hasAttachment?: boolean;
}

interface FilingResult {
  subject: string;
  success: boolean;
  error?: string;
}

const MULTI_SELECT_REQUIREMENT_SET = '1.13';
const LOAD_ITEM_BY_ID_REQUIREMENT_SET = '1.15';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    height: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  card: {
    width: 300,
    padding: 24,
    display: 'flex',
    maxHeight: '90vh',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  logo: {
    width: 200,
    marginBottom: 24,
    alignSelf: 'center',
  },
  field: {
    width: '100%',
    marginBottom: 16,
  },
  searchField: {
    width: '100%',
    marginBottom: 12,
  },
  button: {
    width: '100%',
    marginTop: 8,
  },
  projectListContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    marginBottom: 16,
  },
  projectList: {
    flex: 1,
    overflow: 'auto',
    border: '1px solid #e0e0e0',
    borderRadius: 4,
    backgroundColor: '#fafafa',
    '&::-webkit-scrollbar': {
      width: 8,
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: '#f1f1f1',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#888',
      borderRadius: 4,
      '&:hover': {
        backgroundColor: '#555',
      },
    },
  },
  projectItem: {
    borderBottom: '1px solid #e0e0e0',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#f5f5f5',
    },
    '&.selected': {
      backgroundColor: theme.palette.primary.main,
      color: 'white',
      '&:hover': {
        backgroundColor: theme.palette.primary.dark,
      },
    },
    '&.Mui-disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
  },
  archivedProjectItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
  },
  projectItemText: {
    '& .MuiListItemText-primary': {
      fontSize: 14,
      fontWeight: 500,
    },
    '& .MuiListItemText-secondary': {
      fontSize: 12,
    },
  },
  selectedProjectItemText: {
    '& .MuiListItemText-primary': {
      color: 'white',
    },
    '& .MuiListItemText-secondary': {
      color: 'rgba(255, 255, 255, 0.7)',
    },
  },
  exitButton: {
    marginTop: '10px',
    alignSelf: 'center',
    fontFamily: 'Roboto',
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: '18px',
    lineHeight: '25px',
    textTransform: 'none',
    color: '#007bff',
  },
  statusMessage: {
    width: '280',
    marginTop: 8,
    marginBottom: 8,
  },
  progressText: {
    fontSize: 12,
    color: '#666',
    marginTop: 8,
    textAlign: 'center',
  },
  emptyState: {
    padding: 24,
    textAlign: 'center',
    color: '#666',
  },
  projectCount: {
    fontSize: 12,
    color: '#666',
    marginBottom: 8,
    paddingLeft: 4,
  },
  debugInfo: {
    fontSize: 11,
    color: '#666',
    marginBottom: 12,
    padding: '8px 10px',
    border: '1px solid #e0e0e0',
    borderRadius: 4,
    backgroundColor: '#f8f8f8',
    lineHeight: 1.4,
  },
}));

const KILO_BYTES = 1024;
const MEGA_BYTES = KILO_BYTES * KILO_BYTES;
const MULTI_PART_FILE_SIZE = 5 * MEGA_BYTES;
const MAX_TOTAL_ATTACHMENT_SIZE = 25 * MEGA_BYTES;
const MAX_EMAIL_BODY_SIZE = 2 * MEGA_BYTES;
const EMAIL_DATA_IN_REST_BODY_LIMIT = 100 * KILO_BYTES;

function OutlookAddInLogin() {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [projects, setProjects] = useState<ISimplifiedProjectView[]>([]);
  const [filteredProjects, setFilteredProjects] = useState<ISimplifiedProjectView[]>([]);
  const [currentItem, setCurrentItem] = useState<Office.MessageRead | null>(null);
  const [selectedItems, setSelectedItems] = useState<OfficeSelectedItemDetails[]>([]);
  const [supportsMultiSelect, setSupportsMultiSelect] = useState(false);
  const [supportsLoadItemById, setSupportsLoadItemById] = useState(false);
  const canUseMultiSelect = supportsMultiSelect && supportsLoadItemById;

  const [isLoggedIn, setIsLoggedIn] = useState(false);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [isFiling, setIsFiling] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [user, setUser] = useState<IUser | null>(null);
  const [favoriteProjectIds, setFavouriteProjectIds] = useState<string[]>([]);

  // Status message state
  const [statusMessage, setStatusMessage] = useState<{
    text: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  } | null>(null);
  const [progressMessage, setProgressMessage] = useState<string>('');

  // Auto-hide status messages after 5 seconds
  useEffect(() => {
    if (statusMessage) {
      const timer = setTimeout(() => {
        setStatusMessage(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [statusMessage]);

  // Filter projects based on search query
  useEffect(() => {
    if (searchQuery.trim() === '') {
      const sortedProjects = [...projects].sort((a, b) => projectSort(favoriteProjectIds, a, b));
      setFilteredProjects(sortedProjects);
    } else {
      const query = searchQuery.toLowerCase();
      // Matches the label, so the architect project number finds the project too.
      const filtered = projects.filter((project) =>
        getProjectDisplayName(project).toLowerCase().includes(query),
      );
      const sortedProjects = filtered.sort((a, b) => projectSort(favoriteProjectIds, a, b));
      setFilteredProjects(sortedProjects);
    }
  }, [searchQuery, projects, favoriteProjectIds]);

  useEffect(() => {
    getCurrentUser();
  }, []);

  useEffect(() => {
    const office = (window as any).Office;

    if (!office?.context?.mailbox) {
      return;
    }

    const mailbox = office.context.mailbox;
    const isMultiSelectSupported = Boolean(
      office.context.requirements?.isSetSupported?.('Mailbox', MULTI_SELECT_REQUIREMENT_SET),
    );
    const isLoadItemByIdSupported = Boolean(
      office.context.requirements?.isSetSupported?.('Mailbox', LOAD_ITEM_BY_ID_REQUIREMENT_SET),
    );
    const canUseMultiSelectInClient = isMultiSelectSupported && isLoadItemByIdSupported;
    setSupportsMultiSelect(isMultiSelectSupported);
    setSupportsLoadItemById(isLoadItemByIdSupported);
    let isMounted = true;

    const syncSelection = () => {
      if (!isMounted) {
        return;
      }

      const nextItem = mailbox.item ? (mailbox.item as Office.MessageRead) : null;
      setCurrentItem(nextItem);
      setStatusMessage(null);
      setProgressMessage('');

      if (!canUseMultiSelectInClient || !mailbox.getSelectedItemsAsync) {
        setSelectedItems(
          nextItem?.itemId ? [{ itemId: nextItem.itemId, subject: nextItem.subject }] : [],
        );
        return;
      }

      mailbox.getSelectedItemsAsync((result: Office.AsyncResult<OfficeSelectedItemDetails[]>) => {
        if (!isMounted) {
          return;
        }

        if (result.status === Office.AsyncResultStatus.Succeeded) {
          const items = result.value || [];
          setSelectedItems(
            items.length > 0
              ? items
              : nextItem?.itemId
              ? [{ itemId: nextItem.itemId, subject: nextItem.subject }]
              : [],
          );
        } else {
          console.error('Failed to load selected items:', result.error);
          setSelectedItems(
            nextItem?.itemId ? [{ itemId: nextItem.itemId, subject: nextItem.subject }] : [],
          );
        }
      });
    };

    const handleSelectionChanged = () => {
      syncSelection();
      console.log('Outlook selection changed');
    };

    syncSelection();

    if (mailbox.addHandlerAsync) {
      mailbox.addHandlerAsync(
        Office.EventType.ItemChanged,
        handleSelectionChanged,
        (result: Office.AsyncResult<void>) => {
          if (result.status === Office.AsyncResultStatus.Failed) {
            console.error('Failed to add item changed handler:', result.error);
          }
        },
      );

      if (isMultiSelectSupported) {
        mailbox.addHandlerAsync(
          Office.EventType.SelectedItemsChanged,
          handleSelectionChanged,
          (result: Office.AsyncResult<void>) => {
            if (result.status === Office.AsyncResultStatus.Failed) {
              console.error('Failed to add selected items handler:', result.error);
            }
          },
        );
      }
    }

    return () => {
      isMounted = false;

      if (mailbox.removeHandlerAsync) {
        mailbox.removeHandlerAsync(
          Office.EventType.ItemChanged,
          { handler: handleSelectionChanged },
          () => {
            console.log('Item changed handler removed');
          },
        );
        if (isMultiSelectSupported) {
          mailbox.removeHandlerAsync(
            Office.EventType.SelectedItemsChanged,
            { handler: handleSelectionChanged },
            () => {
              console.log('Selected items handler removed');
            },
          );
        }
      }
    };
  }, []);

  const getCurrentUser = async () => {
    setIsLoading(true);
    try {
      try {
        await Auth.currentSession();
      } catch (e) {
        setIsLoggedIn(false);
        setIsLoading(false);
        return;
      }
      const res = await getLoggedInUser();
      const currentUser = res.user as IUser;
      setUser(currentUser);

      const favoriteIds =
        currentUser.favoriteProjectList?.map((favorite) => favorite.projectId) || [];

      const data = await getProjectsSimplified(4000);

      setProjects(data.results);
      setFavouriteProjectIds(favoriteIds);
      setIsLoggedIn(true);
    } catch (error) {
      setIsLoggedIn(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignOut = () => {
    Auth.signOut().then(() => {
      setIsLoggedIn(false);
      setStatusMessage(null);
      setSearchQuery('');
      setSelectedProjectId(null);
    });
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoggingIn(true);
    setStatusMessage(null);

    if (!email.trim()) {
      setStatusMessage({
        text: 'Email field is empty',
        severity: 'error',
      });
      setIsLoggingIn(false);
      return;
    }

    try {
      await getPublicUserInfoByEmail(email.trim());
      await Auth.signIn(email.trim(), password.trim());
      resetApiClient();
      dispatch(fetchUser());
      await getCurrentUser();
      setStatusMessage({
        text: 'Logged in successfully',
        severity: 'success',
      });
    } catch (error: any) {
      setIsLoggedIn(false);
      setStatusMessage({
        text: error?.message || 'Incorrect username or password',
        severity: 'error',
      });
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleProjectSelect = (projectId: string, hasEmailSlug: boolean) => {
    if (!hasEmailSlug) return;
    setSelectedProjectId(projectId);
    setStatusMessage(null);
  };

  /**
   * Fetches email body asynchronously
   */
  const getEmailBody = async (
    item: Office.MessageRead,
  ): Promise<{ body: string; format: EmailDataBodyFormatEnum }> => {
    return new Promise((resolve, reject) => {
      item.body.getAsync('html', (result: Office.AsyncResult<string>) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve({ body: result.value, format: EmailDataBodyFormatEnum.Html });
        } else {
          // Fallback to text if HTML fails
          item.body.getAsync('text', (textResult: Office.AsyncResult<string>) => {
            if (textResult.status === Office.AsyncResultStatus.Succeeded) {
              resolve({ body: textResult.value, format: EmailDataBodyFormatEnum.Text });
            } else {
              reject(new Error('Failed to retrieve email body'));
            }
          });
        }
      });
    });
  };

  /**
   * Fetches a single attachment's content
   */
  const getAttachmentContent = async (
    item: Office.MessageRead,
    attachment: OfficeAttachment,
  ): Promise<AttachmentContent> => {
    return new Promise((resolve, reject) => {
      item.getAttachmentContentAsync(
        attachment.id,
        (result: Office.AsyncResult<Office.AttachmentContent>) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resolve({
              id: attachment.id,
              name: attachment.name,
              size: attachment.size,
              contentType: attachment.contentType,
              content: result.value.content,
              format: result.value.format,
            });
          } else {
            reject(
              new Error(
                `Failed to load attachment "${attachment.name}": ${
                  result.error?.message || 'Unknown error'
                }`,
              ),
            );
          }
        },
      );
    });
  };

  /**
   * Fetches all attachments in parallel
   */
  const getAllAttachments = async (
    item: Office.MessageRead,
    attachments: OfficeAttachment[],
  ): Promise<AttachmentContent[]> => {
    if (!attachments || attachments.length === 0) {
      return [];
    }

    try {
      const attachmentPromises = attachments.map((attachment) =>
        getAttachmentContent(item, attachment),
      );
      return await Promise.all(attachmentPromises);
    } catch (error) {
      console.error('Error fetching attachments:', error);
      throw error;
    }
  };

  /**
   * Reads all email data from Outlook
   */
  const normalizeOutlookDate = (value: unknown): string | undefined => {
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return value.toISOString();
    }

    if (typeof value === 'string' || typeof value === 'number') {
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString();
    }

    return undefined;
  };

  const readEmailData = async (item: Office.MessageRead): Promise<EmailData> => {
    try {
      // Fetch body
      const { body, format } = await getEmailBody(item);

      // Fetch attachments
      const attachments = (item.attachments as OfficeAttachment[]) || [];
      const attachmentContents = await getAllAttachments(item, attachments);

      // Extract recipient emails with display names
      const toRecipients =
        item.to?.map((r: Office.EmailAddressDetails) =>
          r.displayName ? `${r.displayName} <${r.emailAddress}>` : r.emailAddress,
        ) || [];
      const ccRecipients =
        item.cc?.map((r: Office.EmailAddressDetails) =>
          r.displayName ? `${r.displayName} <${r.emailAddress}>` : r.emailAddress,
        ) || [];
      const messageTimestamp = normalizeOutlookDate(
        (item as Office.MessageRead & { dateTimeCreated?: unknown }).dateTimeCreated,
      );

      const emailData: EmailData = {
        subject: item.subject || '(No Subject)',
        from: {
          emailAddress: item.from?.emailAddress || '',
          displayName: item.from?.displayName,
        },
        to: toRecipients,
        cc: ccRecipients.length > 0 ? ccRecipients : undefined,
        body,
        bodyFormat: format,
        attachments: attachmentContents,
        receivedTime: messageTimestamp,
        sentTime: messageTimestamp,
      };

      return emailData;
    } catch (error) {
      console.error('Error reading email data:', error);
      throw error;
    }
  };

  /**
   * Ensures the "Filed to CENTERLINE" category exists in the master list
   */
  const ensureCenterlineCategory = async (): Promise<void> => {
    return new Promise((resolve, reject) => {
      const office = (window as any).Office;

      if (!office?.context?.mailbox?.masterCategories) {
        reject(new Error('Categories API not available'));
        return;
      }

      // First, check if category already exists
      office.context.mailbox.masterCategories.getAsync(
        (getResult: Office.AsyncResult<Office.CategoryDetails[]>) => {
          if (getResult.status === Office.AsyncResultStatus.Failed) {
            reject(new Error('Failed to get master categories'));
            return;
          }

          const categoryExists = getResult.value.some(
            (cat: Office.CategoryDetails) => cat.displayName === 'In CENTERLINE',
          );

          if (categoryExists) {
            resolve();
            return;
          }

          // Category doesn't exist, add it
          const categoriesToAdd = [
            {
              displayName: 'In CENTERLINE',
              color: Office.MailboxEnums.CategoryColor.Preset22,
            },
          ];

          office.context.mailbox.masterCategories.addAsync(
            categoriesToAdd,
            (addResult: Office.AsyncResult<void>) => {
              if (addResult.status === Office.AsyncResultStatus.Succeeded) {
                console.log('Successfully added "Filed to CENTERLINE" category to master list');
                resolve();
              } else {
                reject(new Error('Failed to add category: ' + addResult.error?.message));
              }
            },
          );
        },
      );
    });
  };

  /**
   * Gets file extension from filename or falls back to content type
   */
  const getFilenameWithExtension = (filename: string, contentType: string): string => {
    // Check if filename already has an extension
    const existingExt = filename.split('.').pop();
    if (existingExt && existingExt.length > 1 && existingExt !== filename) {
      return filename;
    }

    // Map common MIME types to extensions
    const mimeToExt: Record<string, string> = {
      'application/pdf': '.pdf',
      'application/msword': '.doc',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
      'application/vnd.ms-excel': '.xls',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
      'application/vnd.ms-powerpoint': '.ppt',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': '.pptx',
      'image/jpeg': '.jpg',
      'image/png': '.png',
      'image/gif': '.gif',
      'image/tiff': '.tiff',
      'text/plain': '.txt',
      'text/html': '.html',
      'text/csv': '.csv',
      'application/zip': '.zip',
      'application/json': '.json',
      'message/rfc822': '.eml',
    };

    const ext = mimeToExt[contentType] || `.${contentType.split('/')[1] || 'bin'}`;
    return `${filename}${ext}`;
  };

  /**
   * Applies the "In CENTERLINE" category to the current email
   */
  const applyCenterlineCategory = async (item: Office.MessageRead): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (!item?.categories) {
        reject(new Error('Categories API not available on item'));
        return;
      }

      const categoriesToAdd = ['In CENTERLINE'];

      item.categories.addAsync(categoriesToAdd, (result: Office.AsyncResult<void>) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          console.log('Successfully applied "In CENTERLINE" category');
          resolve();
        } else {
          reject(new Error('Failed to apply category: ' + result.error?.message));
        }
      });
    });
  };

  /**
   * Convert base64 string to File object
   */
  const base64ToFile = (base64: string, filename: string, contentType: string): File => {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    const blob = new Blob([bytes], { type: contentType });
    return new File([blob], filename, { type: contentType });
  };

  const getEffectiveSelectedItems = (): OfficeSelectedItemDetails[] => {
    if (!canUseMultiSelect) {
      return currentItem?.itemId
        ? [{ itemId: currentItem.itemId, subject: currentItem.subject }]
        : [];
    }

    if (selectedItems.length > 0) {
      return selectedItems;
    }

    if (currentItem?.itemId) {
      return [{ itemId: currentItem.itemId, subject: currentItem.subject }];
    }

    return [];
  };

  const loadMessageById = async (itemId: string): Promise<Office.MessageRead> => {
    const office = (window as any).Office;
    const mailbox = office?.context?.mailbox;

    if (
      !office?.context?.requirements?.isSetSupported?.('Mailbox', MULTI_SELECT_REQUIREMENT_SET) ||
      !office?.context?.requirements?.isSetSupported?.(
        'Mailbox',
        LOAD_ITEM_BY_ID_REQUIREMENT_SET,
      ) ||
      !mailbox?.loadItemByIdAsync
    ) {
      throw new Error('Outlook does not support loading selected emails in this client.');
    }

    return new Promise((resolve, reject) => {
      mailbox.loadItemByIdAsync(itemId, (result: Office.AsyncResult<Office.MessageRead>) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve(result.value);
        } else {
          reject(new Error(result.error?.message || 'Failed to load selected email.'));
        }
      });
    });
  };

  const unloadMessage = async (item: Office.MessageRead): Promise<void> => {
    const unloadableItem = item as Office.MessageRead & {
      unloadAsync?: (callback?: (result: Office.AsyncResult<void>) => void) => void;
    };

    if (typeof unloadableItem.unloadAsync !== 'function') {
      return;
    }

    return new Promise((resolve) => {
      unloadableItem.unloadAsync?.(() => resolve());
    });
  };

  const fileSingleMessageToProject = async (
    item: Office.MessageRead,
    project: ISimplifiedProjectView,
    messageIndex: number,
    messageCount: number,
  ): Promise<void> => {
    const attachments = (item.attachments as OfficeAttachment[]) || [];

    if (attachments.length > 0) {
      const totalSize = attachments.reduce((sum, attachment) => sum + attachment.size, 0);

      if (totalSize > MAX_TOTAL_ATTACHMENT_SIZE) {
        const totalSizeMB = (totalSize / MEGA_BYTES).toFixed(2);
        throw new Error(`Attachment total (${totalSizeMB} MB) exceeds the 25 MB limit.`);
      }
    }

    setProgressMessage(`Reading email ${messageIndex} of ${messageCount}...`);
    const emailData = await readEmailData(item);

    const emailBodySize = new Blob([emailData.body]).size;
    if (emailBodySize > MAX_EMAIL_BODY_SIZE) {
      const emailBodySizeMB = (emailBodySize / MEGA_BYTES).toFixed(2);
      throw new Error(`Email body size (${emailBodySizeMB} MB) exceeds the 2 MB limit.`);
    }

    let attachmentsWithS3Keys: AttachmentContent[] = [];

    if (emailData.attachments.length > 0) {
      setProgressMessage(
        `Uploading attachments for email ${messageIndex} of ${messageCount} (${emailData.attachments.length} total)...`,
      );

      attachmentsWithS3Keys = await Promise.all(
        emailData.attachments.map(async (attachment, index) => {
          const baseName = attachment.name?.trim() || `attachment-${index + 1}`;
          const fileName = getFilenameWithExtension(baseName, attachment.contentType);

          setProgressMessage(
            `Uploading ${fileName} for email ${messageIndex} of ${messageCount} (${index + 1}/${
              emailData.attachments.length
            })...`,
          );

          const file = base64ToFile(attachment.content, fileName, attachment.contentType);
          const response = await uploadToStaging(
            {
              fileName,
              useMultiPartUpload: attachment.size > MULTI_PART_FILE_SIZE,
            },
            file,
          );

          const verified = await waitForFileToBeVerifiedByUrl(response.s3Key);
          if (!verified) {
            throw new Error(`Failed to verify upload for ${attachment.name}`);
          }

          return {
            ...attachment,
            s3Key: response.s3Key,
            content: '',
          };
        }),
      );
    }

    let emailDataToSend: EmailData = {
      ...emailData,
      attachments: attachmentsWithS3Keys,
    };

    const emailDataSize = new Blob([JSON.stringify(emailDataToSend)]).size;
    if (emailDataSize > EMAIL_DATA_IN_REST_BODY_LIMIT) {
      setProgressMessage(`Uploading email payload ${messageIndex} of ${messageCount}...`);

      const emailDataBlob = new Blob([JSON.stringify(emailDataToSend)], {
        type: 'application/json',
      });
      const emailDataFile = new File([emailDataBlob], `email-data-${Date.now()}.json`, {
        type: 'application/json',
      });

      const emailDataUploadResponse = await uploadToStaging(
        {
          fileName: emailDataFile.name,
          useMultiPartUpload: false,
        },
        emailDataFile,
      );

      const emailDataVerified = await waitForFileToBeVerifiedByUrl(emailDataUploadResponse.s3Key);
      if (!emailDataVerified) {
        throw new Error('Failed to verify email data upload');
      }

      emailDataToSend = {
        subject: emailData.subject,
        from: emailData.from,
        to: emailData.to,
        cc: emailData.cc,
        body: '',
        bodyFormat: emailData.bodyFormat,
        attachments: [],
        receivedTime: emailData.receivedTime,
        sentTime: emailData.sentTime,
        emailDataKey: emailDataUploadResponse.s3Key,
      };
    }

    setProgressMessage(`Filing email ${messageIndex} of ${messageCount}...`);
    await fileEmailToProject(project.id, emailDataToSend);

    try {
      await ensureCenterlineCategory();
      await applyCenterlineCategory(item);
    } catch (categoryError) {
      console.error('Failed to add category:', categoryError);
    }
  };

  const handleFileClick = async () => {
    if (!selectedProjectId) {
      setStatusMessage({
        text: 'Please select a project',
        severity: 'warning',
      });
      return;
    }

    const itemsToProcess = getEffectiveSelectedItems();
    if (itemsToProcess.length === 0) {
      setStatusMessage({
        text: 'Select one or more emails first',
        severity: 'error',
      });
      return;
    }

    if (itemsToProcess.length > 100) {
      setStatusMessage({
        text: 'Outlook allows a maximum of 100 selected messages at a time.',
        severity: 'error',
      });
      return;
    }

    setIsFiling(true);
    setStatusMessage(null);
    setProgressMessage('');

    try {
      const selectedProject = projects.find((p) => p.id === selectedProjectId);
      if (!selectedProject?.emailSlug) {
        throw new Error('The selected project does not have a project email configured.');
      }

      const results: FilingResult[] = [];
      let successfulCount = 0;

      for (let index = 0; index < itemsToProcess.length; index++) {
        const selectedItem = itemsToProcess[index];
        const messageNumber = index + 1;
        let loadedItem: Office.MessageRead | null = null;

        try {
          if (currentItem?.itemId === selectedItem.itemId) {
            loadedItem = currentItem;
          } else {
            setProgressMessage(`Loading email ${messageNumber} of ${itemsToProcess.length}...`);
            loadedItem = await loadMessageById(selectedItem.itemId);
          }

          await fileSingleMessageToProject(
            loadedItem,
            selectedProject,
            messageNumber,
            itemsToProcess.length,
          );

          successfulCount += 1;
          results.push({
            subject: loadedItem.subject || selectedItem.subject || `(Email ${messageNumber})`,
            success: true,
          });
        } catch (error: any) {
          console.error(`Error filing selected email ${selectedItem.itemId}:`, error);
          results.push({
            subject: selectedItem.subject || loadedItem?.subject || `(Email ${messageNumber})`,
            success: false,
            error: error?.message || 'Failed to file email',
          });
        } finally {
          if (loadedItem && loadedItem !== currentItem) {
            await unloadMessage(loadedItem);
          }
        }
      }

      const failedResults = results.filter((result) => !result.success);

      if (failedResults.length === 0) {
        setStatusMessage({
          text:
            successfulCount === 1
              ? 'Email filed successfully'
              : `${successfulCount} emails filed successfully`,
          severity: 'success',
        });
      } else if (successfulCount === 0) {
        setStatusMessage({
          text: failedResults[0].error || 'Failed to file selected emails',
          severity: 'error',
        });
      } else {
        setStatusMessage({
          text: `${successfulCount} filed, ${failedResults.length} failed. First error: ${failedResults[0].error}`,
          severity: 'warning',
        });
      }

      setProgressMessage('');
    } catch (error: any) {
      console.error('Error filing email:', error);
      setStatusMessage({
        text: error?.message || 'Failed to file email',
        severity: 'error',
      });
      setProgressMessage('');
    } finally {
      setIsFiling(false);
    }
  };

  return (
    <div className={classes.root}>
      <Card className={classes.card} elevation={3}>
        <img src={CenterlineIcon} alt="CENTERLINE logo" className={classes.logo} />

        {isLoading ? (
          <CircularLoader />
        ) : (
          <>
            {!isLoggedIn && (
              <>
                <form onSubmit={handleLogin} style={{ width: '100%' }}>
                  <TextField
                    className={classes.field}
                    label="Email"
                    variant="outlined"
                    size="small"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    autoFocus
                  />

                  <TextField
                    className={classes.field}
                    label="Password"
                    type="password"
                    variant="outlined"
                    size="small"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                  />

                  {!isLoggingIn ? (
                    <Button
                      className={classes.button}
                      variant="contained"
                      color="primary"
                      type="submit"
                      disabled={!email || !password}
                    >
                      Log In
                    </Button>
                  ) : (
                    <CircularLoader style={{ marginTop: 16 }} />
                  )}
                </form>

                {statusMessage && (
                  <Alert severity={statusMessage.severity} className={classes.statusMessage}>
                    {statusMessage.text}
                  </Alert>
                )}
              </>
            )}

            {isLoggedIn && (
              <>
                {/* Search Box */}
                <TextField
                  className={classes.searchField}
                  placeholder="Search projects..."
                  variant="outlined"
                  size="small"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Scrollable Project List */}
                <div className={classes.projectListContainer}>
                  <List className={classes.projectList} disablePadding>
                    {filteredProjects.length === 0 ? (
                      <div className={classes.emptyState}>
                        <Typography variant="body2">
                          {searchQuery ? 'No projects found' : 'No projects available'}
                        </Typography>
                      </div>
                    ) : (
                      <>
                        {filteredProjects.map((project) =>
                          project.isArchived ? (
                            <ListItem
                              key={project.id}
                              disabled
                              className={`${classes.projectItem} ${classes.archivedProjectItem}`}
                            >
                              <ListItemText
                                primary={getProjectDisplayName(project)}
                                className={classes.projectItemText}
                              />
                              <ArchivedProjectLock />
                            </ListItem>
                          ) : project.emailSlug ? (
                            <ListItem
                              key={project.id}
                              button
                              className={`${classes.projectItem} ${
                                selectedProjectId === project.id ? 'selected' : ''
                              }`}
                              onClick={() => handleProjectSelect(project.id, true)}
                            >
                              <ListItemText
                                primary={getProjectDisplayName(project)}
                                className={
                                  selectedProjectId === project.id
                                    ? classes.selectedProjectItemText
                                    : classes.projectItemText
                                }
                              />
                            </ListItem>
                          ) : (
                            <ListItem
                              key={project.id}
                              disabled
                              className={classes.projectItem}
                              title="Project email address hasn't been setup for this project."
                            >
                              <ListItemText
                                primary={getProjectDisplayName(project)}
                                className={classes.projectItemText}
                              />
                            </ListItem>
                          ),
                        )}
                      </>
                    )}
                  </List>
                </div>

                {/* File Button */}
                {!isFiling ? (
                  <Button
                    className={classes.button}
                    variant="contained"
                    color="primary"
                    disabled={!selectedProjectId || getEffectiveSelectedItems().length === 0}
                    onClick={handleFileClick}
                  >
                    {canUseMultiSelect && getEffectiveSelectedItems().length > 1
                      ? 'File Selected Emails'
                      : 'File Email'}
                  </Button>
                ) : (
                  <Box style={{ marginTop: 16, width: '100%', textAlign: 'center' }}>
                    <CircularLoader />
                    {progressMessage && (
                      <Typography className={classes.progressText}>{progressMessage}</Typography>
                    )}
                  </Box>
                )}

                {/* Status Message */}
                {statusMessage && (
                  <Alert
                    severity={statusMessage.severity}
                    className={classes.statusMessage}
                    icon={
                      statusMessage.severity === 'success' ? (
                        <CheckCircle />
                      ) : statusMessage.severity === 'error' ? (
                        <ErrorIcon />
                      ) : undefined
                    }
                  >
                    {statusMessage.text}
                  </Alert>
                )}

                {/* Sign Out Button */}
                <Button onClick={handleSignOut} className={classes.exitButton}>
                  <ExitToApp
                    style={{ marginRight: '16px', transform: 'scaleX(-1)', color: '#007bff' }}
                  />
                  Sign Out
                </Button>
              </>
            )}
          </>
        )}
      </Card>
    </div>
  );
}

export default OutlookAddInLogin;
